import { useLocalStorage } from '@uidotdev/usehooks';
import { BarElement, CategoryScale, Chart as ChartJS, Tooltip as ChartTooltip, LinearScale } from 'chart.js';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import Badge from '@/components/Badge/Badge';
import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';

import { BUCKET_COLOR_VARS, NEGATIVE_PRICE_COLOR_VAR } from './config';
import { bucketFor, cheapestWindow, type ElectricitySnapshot, type PriceSlot } from './electricityDerive';

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip);

const HOURS_OPTIONS = [1, 2, 3, 4, 5, 6] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  data?: ElectricitySnapshot;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

function clampHours(v: unknown): number {
  const n = typeof v === 'number' ? v : 3;
  if (n < 1 || n > 6 || !Number.isInteger(n)) return 3;
  return n;
}

function bucketTone(bucket: ReturnType<typeof bucketFor>) {
  if (bucket === 'cheap') return 'success' as const;
  if (bucket === 'expensive') return 'warning' as const;
  return 'info' as const;
}

export default function ElectricityDetailModal({ open, onClose, data, lastUpdated, onRefresh }: Props) {
  const [storedHours, setStoredHours] = useLocalStorage<number>('electricity.cheapestHours', 3);
  const hours = clampHours(storedHours);

  // Ensure stored value is clamped
  if (hours !== storedHours) setStoredHours(hours);

  const nowMs = Date.now();

  const cheapest = useMemo(() => {
    if (!data) return null;
    return cheapestWindow(data.allSlots, nowMs, hours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hours]);

  if (!open || !data) return null;

  return (
    <Modal open={open} onClose={onClose} title="Electricity price" description="Nord Pool spot price (FI)" size="lg">
      <WidgetDetailChrome lastUpdated={lastUpdated} onRefresh={onRefresh} />
      {/* Stats block */}
      <div className="mb-6">
        {/* Current */}
        {data.current && (
          <StatRow label="Current">
            <Badge tone={bucketTone(bucketFor(data.current.cents))}>{data.current.cents.toFixed(2)} ¢/kWh</Badge>
          </StatRow>
        )}

        {/* Today min / max */}
        {data.todayMin && (
          <StatRow label="Today min">
            {data.todayMin.cents.toFixed(2)} ¢ at {fmtTime(data.todayMin.startMs)}
          </StatRow>
        )}
        {data.todayMax && (
          <StatRow label="Today max">
            {data.todayMax.cents.toFixed(2)} ¢ at {fmtTime(data.todayMax.startMs)}
          </StatRow>
        )}

        {/* Cheapest N-h window */}
        <div className="border-b border-dotted border-(--border-subtle) py-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm text-(--text-muted)">Cheapest window</span>
            <HoursSegmentedControl value={hours} onChange={setStoredHours} />
            <span className="ml-auto text-sm text-(--text-primary)">
              {cheapest
                ? `${fmtTime(cheapest.window[0]!.startMs)}–${fmtTime(cheapest.window[cheapest.window.length - 1]!.endMs)} · avg ${cheapest.averageCents.toFixed(2)} ¢/kWh`
                : `—`}
            </span>
          </div>
          {!cheapest && <p className="mt-1 text-xs text-(--text-muted)">Not enough remaining slots for {hours} h</p>}
        </div>

        {/* Next high / low */}
        {data.nextHigh && (
          <StatRow label="Next high">
            {data.nextHigh.cents.toFixed(2)} ¢ at {fmtTime(data.nextHigh.startMs)}
          </StatRow>
        )}
        {data.nextLow && (
          <StatRow label="Next low">
            {data.nextLow.cents.toFixed(2)} ¢ at {fmtTime(data.nextLow.startMs)}
          </StatRow>
        )}

        {/* Tomorrow average */}
        {data.tomorrowAverageCents != null && <StatRow label="Tomorrow avg">{data.tomorrowAverageCents.toFixed(2)} ¢/kWh</StatRow>}
      </div>

      {/* Charts */}
      <div className="space-y-6">
        {data.today.length > 0 && <PriceChart title="Today" slots={data.today} currentSlotStartMs={data.current?.startMs ?? null} />}
        {data.tomorrow.length > 0 && <PriceChart title="Tomorrow" slots={data.tomorrow} currentSlotStartMs={null} />}
      </div>
    </Modal>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-dotted border-(--border-subtle) py-2 text-sm">
      <span className="text-(--text-muted)">{label}</span>
      <span className="text-(--text-primary)">{children}</span>
    </div>
  );
}

function HoursSegmentedControl({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const idx = HOURS_OPTIONS.indexOf(value as (typeof HOURS_OPTIONS)[number]);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = HOURS_OPTIONS[Math.min(idx + 1, HOURS_OPTIONS.length - 1)]!;
      onChange(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = HOURS_OPTIONS[Math.max(idx - 1, 0)]!;
      onChange(prev);
    }
  };

  return (
    <div
      role="group"
      aria-label="Cheapest hours window"
      className="flex gap-0.5 rounded-md bg-(--bg-surface) p-0.5"
      onKeyDown={handleKeyDown}
    >
      {HOURS_OPTIONS.map((h) => (
        <button
          key={h}
          type="button"
          aria-pressed={h === value}
          onClick={() => onChange(h)}
          className={`rounded px-2 py-0.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--border-focus) ${
            h === value ? 'bg-(--accent-primary)/20 text-(--accent-primary)' : 'text-(--text-muted) hover:text-(--text-secondary)'
          }`}
        >
          {h}h
        </button>
      ))}
    </div>
  );
}

function PriceChart({ title, slots, currentSlotStartMs }: { title: string; slots: PriceSlot[]; currentSlotStartMs: number | null }) {
  const resolvedColors = useMemo(() => {
    const style = getComputedStyle(document.documentElement);
    return {
      cheap: style.getPropertyValue(BUCKET_COLOR_VARS.cheap).trim(),
      normal: style.getPropertyValue(BUCKET_COLOR_VARS.normal).trim(),
      expensive: style.getPropertyValue(BUCKET_COLOR_VARS.expensive).trim(),
      negative: style.getPropertyValue(NEGATIVE_PRICE_COLOR_VAR).trim(),
      focus: style.getPropertyValue('--border-focus').trim(),
      muted: style.getPropertyValue('--text-muted').trim(),
    };
  }, []);

  const chartData = useMemo(() => {
    const { cheap, normal, expensive } = resolvedColors;
    const colors = { cheap, normal, expensive };
    const focusColor = resolvedColors.focus;

    const labels = slots.map((s) => {
      const d = new Date(s.startMs);
      const h = d.getHours();
      const m = d.getMinutes();
      // Show label every 3 hours at :00
      if (m === 0 && h % 3 === 0) return `${String(h).padStart(2, '0')}:00`;
      return '';
    });

    const backgroundColors = slots.map((s) => (s.cents < 0 ? resolvedColors.negative : colors[bucketFor(s.cents)]));
    const borderColors = slots.map((s) => (s.startMs === currentSlotStartMs ? focusColor : 'transparent'));
    const borderWidths = slots.map((s) => (s.startMs === currentSlotStartMs ? 2 : 0));

    return {
      labels,
      datasets: [
        {
          data: slots.map((s) => s.cents),
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: borderWidths,
          barPercentage: 1,
          categoryPercentage: 1,
        },
      ],
    };
  }, [slots, currentSlotStartMs, resolvedColors]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (ctx: { dataIndex: number }[]) => {
              const idx = ctx[0]?.dataIndex ?? 0;
              const s = slots[idx];
              return s ? fmtTime(s.startMs) : '';
            },
            label: (ctx: { raw: unknown }) => `${(ctx.raw as number).toFixed(2)} ¢/kWh`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: resolvedColors.muted,
            font: { size: 10 },
            maxRotation: 0,
          },
        },
        y: {
          grid: { color: 'rgba(255,255,255,0.04)' },
          ticks: {
            color: resolvedColors.muted,
            font: { size: 10 },
            callback: (v: string | number) => `${v}`,
          },
          beginAtZero: true,
        },
      },
    }),
    [slots, resolvedColors],
  );

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-(--text-muted) uppercase">{title}</h3>
      <div className="h-36">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Bar data={chartData} options={options as any} />
      </div>
    </div>
  );
}
